import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Tag, Store, Calendar, TrendingDown, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export const DealsPage = () => {
    const navigate = useNavigate();
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDeals();
    }, []);

    const loadDeals = async () => {
        try {
            setLoading(true);
            const res = await api.getDeals();
            setDeals(res.data);
        } catch (error) {
            console.error('Error loading deals:', error);
        } finally {
            setLoading(false);
        }
    };

    // Group deals by store
    const dealsByStore = deals.reduce((acc, deal) => {
        const store = deal.store_name || 'Autre';
        if (!acc[store]) acc[store] = [];
        acc[store].push(deal);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-orange-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                
                {/* Header */}
                <div className="mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/')}
                        className="mb-4 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à l'accueil
                    </Button>
                    
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 bg-red-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <Tag className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-black text-foreground">
                                Rabais de la Semaine
                            </h1>
                            <p className="text-muted-foreground">
                                Les meilleures offres pour économiser
                            </p>
                        </div>
                    </div>

                    {/* Stats banner */}
                    <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-4 text-white border-2 border-foreground shadow-pop">
                        <div className="flex items-center justify-around">
                            <div className="text-center">
                                <p className="text-3xl font-black">{deals.length}</p>
                                <p className="text-sm opacity-90">Rabais actifs</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-black">{Object.keys(dealsByStore).length}</p>
                                <p className="text-sm opacity-90">Magasins</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-black">
                                    {deals.length > 0 ? Math.max(...deals.map(d => d.discount_percent || 0)) : 0}%
                                </p>
                                <p className="text-sm opacity-90">Max rabais</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Deals List */}
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-24 bg-white rounded-2xl animate-pulse border-2 border-gray-100" />
                        ))}
                    </div>
                ) : deals.length > 0 ? (
                    <div className="space-y-6">
                        {Object.entries(dealsByStore).map(([store, storeDeals]) => (
                            <div key={store} className="bg-white rounded-2xl border-2 border-foreground shadow-pop overflow-hidden">
                                {/* Store header */}
                                <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 py-3 border-b-2 border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <Store className="w-5 h-5 text-gray-600" />
                                        <h2 className="font-heading font-bold text-lg">{store}</h2>
                                        <span className="ml-auto bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-sm font-bold">
                                            {storeDeals.length} rabais
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Deals */}
                                <div className="divide-y divide-gray-100">
                                    {storeDeals.map((deal) => (
                                        <div 
                                            key={deal.id} 
                                            className="flex items-center gap-4 p-4 hover:bg-red-50 transition-colors"
                                        >
                                            {/* Discount badge */}
                                            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center text-white font-black text-lg border-2 border-foreground shadow-md">
                                                -{deal.discount_percent}%
                                            </div>
                                            
                                            {/* Product info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-foreground truncate">{deal.product_name}</p>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <span className="text-gray-400">{deal.category}</span>
                                                    {deal.valid_until && (
                                                        <>
                                                            <span>•</span>
                                                            <Calendar className="w-3 h-3" />
                                                            <span>Jusqu'au {new Date(deal.valid_until).toLocaleDateString('fr-CA')}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Prices */}
                                            <div className="text-right flex-shrink-0">
                                                <p className="font-black text-xl text-green-600">{deal.sale_price?.toFixed(2)}$</p>
                                                <p className="text-sm text-gray-400 line-through">{deal.original_price?.toFixed(2)}$</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
                        <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-500 mb-2">Aucun rabais disponible</h3>
                        <p className="text-muted-foreground">Revenez bientôt pour découvrir les nouvelles offres!</p>
                    </div>
                )}

                {/* CTA */}
                <div className="mt-8 text-center">
                    <p className="text-muted-foreground mb-4">
                        Ces rabais sont intégrés automatiquement dans votre Pack Hebdomadaire Premium
                    </p>
                    <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-xl border-2 border-foreground shadow-pop"
                    >
                        <TrendingDown className="w-5 h-5 mr-2" />
                        Découvrir Premium
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DealsPage;
